import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowUp, ArrowDown, Minus, ArrowClockwise, CalendarBlank, TrendUp, Handshake, CheckCircle, ClockCounterClockwise, CaretDown, CaretUp, Fire, Hourglass } from "@phosphor-icons/react";

const isoToday = () => new Date().toISOString().slice(0, 10);
const addDays = (isoDate, days) => {
  const t = new Date(isoDate + "T00:00:00Z");
  t.setUTCDate(t.getUTCDate() + days);
  return t.toISOString().slice(0, 10);
};
const daysBetween = (fromIso, toIso) => {
  const a = new Date(fromIso + "T00:00:00Z");
  const b = new Date(toIso + "T00:00:00Z");
  return Math.round((b - a) / 86400000) + 1; // inclusive
};

const fmtINR = (v) => {
  const n = Number(v || 0);
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(2)}Cr`;
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(2)}L`;
  if (n >= 1e3) return `₹${(n / 1e3).toFixed(1)}K`;
  return `₹${n.toLocaleString("en-IN")}`;
};

const fmtNum = (n) => Number(n || 0).toLocaleString("en-IN");
const fmtRange = (iso) => {
  const d = new Date(iso);
  return d.toLocaleDateString("en-IN", { month: "short", day: "numeric" });
};

// Delta pill: green ↑ / red ↓ / grey — . For KPIs where an INCREASE is bad
// (cycle time, stale, time-in-stage), pass `inverse` to flip the colors so a
// 200% increase shows RED and a decrease shows GREEN.
const DeltaPill = ({ pct, inverse = false, testid }) => {
  const isFlat = !pct || Math.abs(pct) < 0.1;
  const isUp = !isFlat && pct > 0;
  const good = inverse ? !isUp : isUp;
  const bg = isFlat ? "bg-slate-100 text-slate-500" : good ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700";
  const Icon = isFlat ? Minus : isUp ? ArrowUp : ArrowDown;
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-sm ${bg}`}>
      <Icon size={9} weight="bold"/>
      {isFlat ? "flat" : `${Math.abs(pct).toFixed(1)}%`}
    </span>
  );
};

const KpiCard = ({ icon: Icon, label, thisValue, lastValue, deltaPct, formatValue, subLabel, inverse, testid }) => (
  <div className="bg-white border border-slate-200 rounded-sm p-4" data-testid={testid}>
    <div className="flex items-center gap-1.5 label-micro text-slate-600">
      <Icon size={12} className="text-blue-600"/>{label}
    </div>
    <div className="flex items-baseline justify-between gap-2 mt-2">
      <div className="font-display text-2xl font-black text-slate-900 tabular-nums" data-testid={`${testid}-value`}>{formatValue(thisValue)}</div>
      <DeltaPill pct={deltaPct} inverse={inverse} testid={`${testid}-delta`}/>
    </div>
    <div className="text-[11px] text-slate-500 mt-1">
      {subLabel || <>Last week: <span className="font-semibold text-slate-700">{formatValue(lastValue)}</span></>}
    </div>
  </div>
);

const StageRow = ({ s }) => (
  <li className="grid grid-cols-12 gap-2 items-center px-4 py-2.5 border-b border-slate-100 last:border-b-0 text-sm hover:bg-slate-50/60" data-testid={`week-stage-${s.id}`}>
    <div className="col-span-3 flex items-center gap-2 min-w-0">
      <span className={`inline-block w-1.5 h-1.5 rounded-full shrink-0 ${s.is_closed ? (s.is_won ? "bg-emerald-500" : "bg-red-400") : "bg-blue-500"}`}/>
      <span className="font-semibold text-slate-900 truncate">{s.label}</span>
    </div>
    <div className="col-span-2 text-right tabular-nums">
      <div className="text-xs text-slate-500">{fmtNum(s.this_count)} deals</div>
      <div className="font-bold text-slate-900">{fmtINR(s.this_value)}</div>
    </div>
    <div className="col-span-2 text-right tabular-nums">
      <div className="text-xs text-slate-400">{fmtNum(s.last_count)} deals</div>
      <div className="text-slate-500">{fmtINR(s.last_value)}</div>
    </div>
    <div className="col-span-1 flex justify-end"><DeltaPill pct={s.count_delta_pct} testid={`week-stage-${s.id}-count-delta`}/></div>
    <div className="col-span-1 flex justify-end"><DeltaPill pct={s.value_delta_pct} testid={`week-stage-${s.id}-value-delta`}/></div>
    <div className="col-span-2 text-right tabular-nums" data-testid={`week-stage-${s.id}-avg-days`}>
      <div className="font-bold text-slate-900">{(s.avg_days_in_stage_this || 0).toFixed(1)}d</div>
      <div className="text-[11px] text-slate-500 flex items-center justify-end gap-1">
        vs <span className="text-slate-600">{(s.avg_days_in_stage_last || 0).toFixed(1)}d</span>
        <DeltaPill pct={s.avg_days_in_stage_delta_pct} inverse testid={`week-stage-${s.id}-tis-delta`}/>
      </div>
    </div>
  </li>
);

export default function WeeklyImprovementCard() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const [showClosed, setShowClosed] = useState(false);
  // Custom date range. Default: last 7 days ending today.
  const [startDate, setStartDate] = useState(() => addDays(isoToday(), -6));
  const [endDate, setEndDate] = useState(() => isoToday());

  const rangeDays = useMemo(() => daysBetween(startDate, endDate), [startDate, endDate]);
  const invalid = rangeDays <= 0;

  const load = async (s = startDate, e = endDate) => {
    if (daysBetween(s, e) <= 0) return;
    setBusy(true); setError(null);
    try {
      const q = `?start_date=${encodeURIComponent(s)}&end_date=${encodeURIComponent(e)}`;
      const { data: resp } = await api.get(`/crm/weekly-improvement${q}`);
      setData(resp);
    } catch (e2) {
      const d = e2?.response?.data?.detail;
      const msg = typeof d === "string" ? d : d?.message;
      setError(msg || "Sales KPI card temporarily unavailable — retry in a moment.");
    } finally { setBusy(false); }
  };
  useEffect(() => { if (!invalid) load(startDate, endDate); /* eslint-disable-next-line */ }, [startDate, endDate]);

  const applyPreset = (days) => {
    const today = isoToday();
    setEndDate(today);
    setStartDate(addDays(today, -(days - 1)));
  };

  const stagesVisible = useMemo(() => {
    if (!data?.stages) return [];
    return showClosed ? data.stages : data.stages.filter(s => !s.is_closed);
  }, [data, showClosed]);

  if (error) {
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-sm p-4 text-sm text-blue-900 flex items-start justify-between gap-3" data-testid="weekly-tracker-empty">
        <div className="flex items-center gap-2">
          <TrendUp size={16} className="text-blue-700"/>
          <div>
            <div className="font-semibold">Sales KPI card</div>
            <div className="text-xs text-blue-800/80 mt-0.5">{error}</div>
          </div>
        </div>
        <button onClick={() => load()} disabled={busy} data-testid="weekly-tracker-retry" className="text-xs inline-flex items-center gap-1 border border-blue-300 rounded-sm px-2.5 py-1 hover:bg-blue-100 disabled:opacity-50">
          <ArrowClockwise size={11} className={busy ? "animate-spin" : ""}/>{busy ? "Loading" : "Retry"}
        </button>
      </div>
    );
  }
  if (!data && busy) return <div className="text-xs text-slate-500 py-2" data-testid="weekly-tracker-loading">Loading Sales KPI card…</div>;
  if (!data) return null;

  const k = data.kpis || {};
  return (
    <section className="bg-white border border-slate-200 rounded-sm" data-testid="weekly-improvement-tracker">
      <header className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between px-3 sm:px-5 py-3 border-b border-slate-200">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <TrendUp size={16} className="text-blue-600"/>
            <h2 className="font-display font-bold text-slate-900 text-sm sm:text-base">Sales KPI card</h2>
            <span className="text-[10px] font-semibold uppercase text-slate-400 tracking-wider">{rangeDays}-day range</span>
          </div>
          <div className="hidden sm:flex items-center gap-2 text-xs text-slate-500 mt-1 flex-wrap">
            <CalendarBlank size={11}/>
            <span data-testid="week-range">Comparing <b className="text-slate-700">{fmtRange(data.this_period_start || data.this_week_start)} – {fmtRange(data.this_period_end || data.this_week_end)}</b> vs previous <b className="text-slate-700">{data.period_days || rangeDays}</b> days <b className="text-slate-700">{fmtRange(data.last_period_start || data.last_week_start)} – {fmtRange(data.last_period_end || data.last_week_end)}</b></span>
            {data.pipeline_label && <span className="text-slate-400">· {data.pipeline_label}</span>}
            {data.data_source === "internal_crm" && (
              <span data-testid="kpi-data-source-badge" className="text-[10px] font-bold uppercase text-amber-700 bg-amber-100 border border-amber-200 px-1.5 py-0.5 rounded-sm">
                Internal CRM
              </span>
            )}
          </div>
          <div className="sm:hidden text-[11px] text-slate-500 mt-1">
            {fmtRange(data.this_period_start || data.this_week_start)} – {fmtRange(data.this_period_end || data.this_week_end)}
            {data.data_source === "internal_crm" && <span className="ml-1 text-[9px] font-bold uppercase text-amber-700 bg-amber-100 px-1 rounded-sm">Internal CRM</span>}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap" data-testid="date-range-picker">
          <div className="inline-flex items-center gap-1 w-full sm:w-auto">
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} max={endDate} disabled={busy} data-testid="range-start" aria-label="Start date"
                   className="flex-1 sm:flex-none border border-slate-300 rounded-sm px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"/>
            <span className="text-slate-400 text-xs">→</span>
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} min={startDate} max={isoToday()} disabled={busy} data-testid="range-end" aria-label="End date"
                   className="flex-1 sm:flex-none border border-slate-300 rounded-sm px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"/>
          </div>
          <div className="inline-flex items-center gap-1">
            <button onClick={() => applyPreset(7)}  disabled={busy} data-testid="range-preset-7"  className="text-[11px] px-2 py-1 border border-slate-300 rounded-sm hover:bg-slate-100 disabled:opacity-50">7d</button>
            <button onClick={() => applyPreset(30)} disabled={busy} data-testid="range-preset-30" className="text-[11px] px-2 py-1 border border-slate-300 rounded-sm hover:bg-slate-100 disabled:opacity-50">30d</button>
            <button onClick={() => applyPreset(90)} disabled={busy} data-testid="range-preset-90" className="text-[11px] px-2 py-1 border border-slate-300 rounded-sm hover:bg-slate-100 disabled:opacity-50">90d</button>
            <button onClick={() => load()} disabled={busy || invalid} data-testid="weekly-tracker-refresh" className="text-xs inline-flex items-center gap-1 border border-slate-300 rounded-sm px-2 py-1 hover:bg-slate-100 disabled:opacity-50">
              <ArrowClockwise size={11} className={busy ? "animate-spin" : ""}/><span className="hidden sm:inline">{busy ? "Refreshing" : "Refresh"}</span>
            </button>
            <button onClick={() => setCollapsed(!collapsed)} data-testid="weekly-tracker-collapse" className="text-xs inline-flex items-center gap-1 border border-slate-300 rounded-sm px-2 py-1 hover:bg-slate-100">
              {collapsed ? <CaretDown size={11}/> : <CaretUp size={11}/>}
            </button>
          </div>
        </div>
      </header>

      {!collapsed && (
        <div className="p-3 sm:p-5 space-y-5">
          {/* Top-strip KPIs — 2×3 on mobile, 3×2 on tablet, 6 across on desktop */}
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-2 sm:gap-3" data-testid="week-kpis">
            <KpiCard icon={Handshake} label="New deals"
              thisValue={k.new_deals?.this} lastValue={k.new_deals?.last}
              deltaPct={k.new_deals?.delta_pct} formatValue={fmtNum}
              testid="kpi-new-deals"/>
            <KpiCard icon={TrendUp} label="Deals moved forward"
              thisValue={k.deals_moved?.this} lastValue={k.deals_moved?.last}
              deltaPct={k.deals_moved?.delta_pct} formatValue={fmtNum}
              testid="kpi-deals-moved"/>
            <KpiCard icon={CheckCircle} label="Closed won"
              thisValue={k.closed_won_value?.this} lastValue={k.closed_won_value?.last}
              deltaPct={k.closed_won_value?.delta_pct} formatValue={fmtINR}
              subLabel={<>Last week: <span className="font-semibold text-slate-700">{fmtINR(k.closed_won_value?.last)}</span> · {fmtNum(k.closed_won_count?.this)} won</>}
              testid="kpi-closed-won"/>
            <KpiCard icon={ClockCounterClockwise} label="Avg proposal → close (days)"
              thisValue={k.avg_cycle_days?.this} lastValue={k.avg_cycle_days?.last}
              deltaPct={k.avg_cycle_days?.delta_pct}
              formatValue={(v) => (v && v > 0) ? `${v.toFixed(1)}d` : "—"}
              subLabel={<>Lifetime avg from proposal sent → closed-won. Prev snapshot: <span className="font-semibold text-slate-700">{k.avg_cycle_days?.last > 0 ? `${k.avg_cycle_days.last.toFixed(1)}d` : "—"}</span></>}
              inverse
              testid="kpi-avg-cycle"/>
            <KpiCard icon={Fire} label="Newly aged > 30d"
              thisValue={k.newly_stale?.this} lastValue={k.newly_stale?.last}
              deltaPct={k.newly_stale?.delta_pct} formatValue={fmtNum}
              subLabel={<>Last week: <span className="font-semibold text-slate-700">{fmtNum(k.newly_stale?.last)}</span> · leading indicator</>}
              inverse
              testid="kpi-newly-stale"/>
            <KpiCard icon={Hourglass} label="Avg time in stage"
              thisValue={k.avg_time_in_stage?.this} lastValue={k.avg_time_in_stage?.last}
              deltaPct={k.avg_time_in_stage?.delta_pct} formatValue={(v)=>`${(v||0).toFixed(1)}d`}
              subLabel={<>Last week: <span className="font-semibold text-slate-700">{(k.avg_time_in_stage?.last || 0).toFixed(1)}d</span> · open deals snapshot</>}
              inverse
              testid="kpi-time-in-stage"/>
          </div>

          {/* Per-stage table — horizontal scroll on narrow screens so the
              12-col grid doesn't collapse into a stacked mess. */}
          <div>
            <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
              <h3 className="font-display font-bold text-sm text-slate-900">Movement by stage</h3>
              <label className="text-xs text-slate-600 inline-flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={showClosed} onChange={(e) => setShowClosed(e.target.checked)} data-testid="week-toggle-closed"/>
                Include closed stages
              </label>
            </div>
            <div className="border border-slate-200 rounded-sm overflow-x-auto">
              <div className="min-w-[560px]">
              <div className="grid grid-cols-12 gap-2 px-4 py-2 border-b border-slate-200 bg-slate-50/70 label-micro text-slate-500">
                <div className="col-span-3">Stage</div>
                <div className="col-span-2 text-right">This week</div>
                <div className="col-span-2 text-right">Last week</div>
                <div className="col-span-1 text-right" title="Change in deal count vs last week">Δ Deals</div>
                <div className="col-span-1 text-right" title="Change in deal value vs last week">Δ Value</div>
                <div className="col-span-2 text-right">Avg time in stage</div>
              </div>
              {stagesVisible.length === 0 ? (
                <div className="px-4 py-6 text-center text-sm text-slate-400">No stage movement recorded this week yet.</div>
              ) : (
                <ul className="max-h-80 overflow-y-auto" data-testid="week-stage-list">
                  {stagesVisible.map(s => <StageRow key={s.id} s={s}/>)}
                </ul>
              )}
              </div>
            </div>
            <p className="text-[11px] text-slate-400 mt-2">Scanned {fmtNum(data.fetched_deal_count)} deals · {data.week_convention}</p>
          </div>
        </div>
      )}
    </section>
  );
}
